/**
 * The organization half: the one organization you administer, what its seats
 * cost, and its seat list.
 *
 * **There is no form here that creates one, and there is no second one to
 * create.** An account holds a single organization, chosen at sign-up, and this
 * page is that organization's seats and its invoice. The page used to open with
 * *Create an organization* whoever was looking at it, which sent somebody who
 * had named theirs while signing up to a form offering to make another; moving
 * it to a *Create another organization* button at the foot only made the offer
 * quieter, not truer. `postOrg` refuses the second one, so the button was a
 * door onto a refusal.
 *
 * Reached only by somebody who administers an organization — the shell hides
 * the group otherwise and refuses the page behind it (`App.tsx`) — so the role
 * check here is the second of two rather than the only one. It stays because
 * the two answer different questions: the shell asks whether these pages exist
 * for this person, and this asks what they may do on the one in front of them.
 *
 * Inviting is an *invitation* rather than an assignment. The seat is created at
 * once, the mail goes out, and the address it went to decides for itself — an
 * account holder who was never asked should not find themselves in somebody
 * else's tenant. Where mail cannot be sent, the link is shown to whoever sent
 * the invitation, because a seat is a row and not an email.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font, radius, type as t } from '../theme/tokens';
import { Badge, Callout, Card, Empty, GhostButton, Input, PrimaryButton, Row, RowAction, StatStrip, StatusCell, Table } from '../components/primitives';
import { Modal, useConfirm } from '../components/Modal';
import { MenuButton } from '../components/MenuButton';
import { api, ApiError, apiPath } from '../lib/api';
import { hasBoughtSeats, seatFigures } from '../lib/billing';
import { isoDay } from '../lib/format';
import type { Invitation, Me, Organization, Plan, Seat } from '../lib/types';
import { HandOver, RoleCell } from './org-roles';
import { BillingHistory } from './Member';
import { BillingStatus, EnterpriseUpgrade, PeriodSwitch, TRIAL_PLAN, TrialOffer, trialOffered } from './org-billing';
import { InviteResult } from './invite-result';
import { DateCell } from '../components/DateCell';
import { PageError, SkeletonRows, useLoadPhase } from '../components/Loading';

/** The organization this account administers, which is the one every seat page
 *  is about. Owner before administrator, because an owner of one and an
 *  administrator of another should land on their own. */
export function administered(me: Me): Organization | null {
  return (
    me.organizations.find((organization) => organization.role === 'owner') ??
    me.organizations.find((organization) => organization.role === 'admin') ??
    null
  );
}

/** The badge beside each person: their state, in the fewest words that
 *  distinguish it from the other three. */
const SEAT_STATE: Record<string, { word: string; tone: 'ok' | 'warn' | 'off'; ring?: boolean }> = {
  assigned: { word: 'Seat', tone: 'ok' },
  member: { word: 'No seat', tone: 'warn' },
  suspended: { word: 'Deactivated', tone: 'off', ring: true },
  invited: { word: 'Invited', tone: 'off', ring: true }
};

/**
 * What a seat is worth today, in one phrase beside its state.
 *
 * The page underneath carries a licence row per seat — a serial, a key, a
 * revocation — and none of that belongs on a list of people. What an owner is
 * asking is whether this person can work, so every answer is said as something
 * that happens to a person rather than to a licence.
 *
 * **Short enough to sit on one line beside the badge**, which is a column in a
 * table and not a paragraph. They were sentences — "here — Airlock unlocks
 * when they are given a seat" — and they wrapped to three lines and pushed the
 * badge out of shape. What the state *means* belongs under the table, where
 * there is room for it, and this is the half-dozen words that distinguish one
 * row from the next.
 */
const SEAT_WORD = (seat: Seat): string => {
  if (seat.seatStatus === 'suspended') return 'place kept';
  if (seat.seatStatus === 'member') return 'none given yet';
  if (seat.seatStatus !== 'assigned') return 'not accepted yet';
  switch (seat.license?.access) {
    case 'active':
      return 'working';
    case 'grace':
      return 'past due';
    case 'lapsed':
      return 'lapsed';
    case 'revoked':
      return 'withdrawn';
    default:
      return 'no key yet';
  }
};

/** What a team pays this period: the price per seat times the seats it holds. */
function monthlyTotal(plan: Plan | undefined, seats: number): string {
  if (!plan) return '—';
  return `$${plan.priceUsd * Math.max(1, seats)}`;
}

/**
 * Seats, as something to buy.
 *
 * One rule, said once here and enforced in `membership.ts`: **an organization
 * buys seats here and gives them out on Members.** Nothing on that page spends
 * money and nothing on this one names a person — so an owner who wants to
 * spend less comes here, and an owner deciding who works comes here first and
 * goes there second. Inviting costs nothing at all, which is what lets this
 * page be the only door onto an invoice.
 */
export function TeamPlan({ me, organization }: { me: Me; organization: Organization }): React.JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { billing } = organization;
  // From the subscription, not from access: a beta grant is active and has
  // bought nothing, so it is offered the plans (`lib/billing.ts`).
  const bought = hasBoughtSeats(billing);
  // Priced from the organization's own tier: an Enterprise organization read
  // the Team price here before the redesign caught it (README §4).
  const tierPlans = billing.tier === 'enterprise' ? me.config.enterprisePlans : me.config.seatPlans;
  const current = tierPlans.find((plan) => plan.id === billing.plan);

  const buy = async (plan: string): Promise<void> => {
    setBusy(plan);
    setError(null);
    try {
      const answer = await api.post<{ url: string }>(apiPath`/orgs/${organization.id}/checkout`, { plan });
      window.location.href = answer.url;
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
      setBusy(null);
    }
  };

  const portal = async (): Promise<void> => {
    setBusy('portal');
    setError(null);
    try {
      const answer = await api.post<{ url: string }>(apiPath`/orgs/${organization.id}/billing/portal`);
      window.location.href = answer.url;
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
      setBusy(null);
    }
  };

  if (bought) {
    return (
      <>
        <StatStrip
          items={[
            { label: 'Seats held', value: String(organization.seats) },
            { label: 'Seats billed', value: String(billing.seatsPaid) },
            { label: 'Per seat', value: current ? `$${current.priceUsd}` : '—' },
            { label: 'This period', value: monthlyTotal(current, billing.seatsPaid) }
          ]}
        />
        <div style={{ height: 20 }} />
        <Card style={{ maxWidth: 620 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>{current?.label ?? 'Team'}</span>
            <Badge tone={organization.seatAccess === 'active' ? 'ok' : organization.seatAccess === 'grace' ? 'warn' : 'off'}>
              {organization.seatAccess}
            </Badge>
            {billing.cancelAtPeriodEnd && <Badge tone="warn">ends at period end</Badge>}
          </div>
          <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '10px 0 0', lineHeight: 1.6 }}>
            {organization.name} is billed for {billing.seatsPaid} seat{billing.seatsPaid === 1 ? '' : 's'}
            {billing.paidThrough ? `, paid through ${isoDay(billing.paidThrough)}` : ''}. Buying one charges the rest of
            this period for it; giving one back leaves a credit against the next invoice. Who holds them is decided on
            Members, and putting somebody in the organization costs nothing.
          </p>
          {organization.seats < billing.seatsPaid && (
            <p style={{ fontSize: 13, color: color.inkSubtle, margin: '12px 0 0', lineHeight: 1.6 }}>
              {billing.seatsPaid - organization.seats} of them {billing.seatsPaid - organization.seats === 1 ? 'is' : 'are'}{' '}
              free to give to somebody on Members.
            </p>
          )}
          {error && <p style={{ fontSize: 13.5, color: color.badText, margin: '14px 0 0' }}>{error}</p>}
          <div style={{ marginTop: 20 }}>
            <GhostButton onClick={() => void portal()}>
              {busy === 'portal' ? 'Opening' : 'Card, VAT details and invoices'}
            </GhostButton>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="al-two" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 20 }}>
        {tierPlans.map((plan) => (
          <Card key={plan.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18, fontWeight: 600 }}>{plan.label}</span>
              {plan.id === 'annual' && <Badge tone="warn">Save 17%</Badge>}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 18 }}>
              <span style={{ fontSize: t.priceApp, letterSpacing: '-.03em', fontWeight: 600 }}>${plan.priceUsd}</span>
              <span style={{ fontSize: 14, color: color.inkSubtle }}>{plan.period} excl. VAT</span>
            </div>
            <div style={{ fontSize: 13.5, color: color.inkSubtle, marginTop: 8, lineHeight: 1.6 }}>
              {organization.seats} seat{organization.seats === 1 ? '' : 's'} today —{' '}
              <strong style={{ fontWeight: 600 }}>{monthlyTotal(plan, organization.seats)}</strong>{' '}
              {plan.id === 'annual' ? 'a year' : 'a month'} to start. Every invitation after that is prorated.
            </div>
            {!plan.onSale ? (
              <div style={{ fontSize: 13, color: color.warnText, marginTop: 12 }}>
                Not on sale on this deployment — no Stripe price is configured for seats.
              </div>
            ) : (
              <div style={{ marginTop: 20 }}>
                <PrimaryButton full size="lg" onClick={() => void buy(plan.id)}>
                  {busy === plan.id ? 'Buying' : `Buy seats ${plan.id === 'annual' ? 'yearly' : 'monthly'}`}
                </PrimaryButton>
              </div>
            )}
          </Card>
        ))}
      </div>
      {error && <p style={{ fontSize: 13.5, color: color.badText, marginTop: 16 }}>{error}</p>}
      <Card style={{ marginTop: 20, maxWidth: 620 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>What a seat is</div>
        <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '6px 0 0', lineHeight: 1.6 }}>
          One perpetual key per person, on up to two machines each, and one invoice for the organization.{' '}
          {organization.name} holds {organization.seats} today. Nothing here is given out by itself — buying puts seats
          in the organization, and Members is where each one is handed to somebody, your own included. Anybody may be
          invited whether or not there is a seat for them: they join, they are in their teams, and Airlock unlocks on
          their machine the day they are given one. Buy for the people you have and the ones you are about to hire.
        </p>
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '14px 0 0', lineHeight: 1.6 }}>
          Payment, VAT and invoices are Stripe’s, as they are for a personal licence.
        </p>
      </Card>
    </>
  );
}

/**
 * Billing — what this organization buys, and the one page that changes it.
 *
 * It used to be a page of **links**: *Seats & invites* went to Members, *Licence
 * & plan* went to a page the organization shell does not even hold (an owner
 * landed back on their overview), and a dark panel repeated every organization
 * with a *Manage seats* button on each. Four doors, none of which was the thing
 * somebody came here to do, and no way at all to buy a seat or give one back —
 * the count followed the seat list, so the only way to pay for less was to
 * remove a person.
 *
 * What is here now is the money and nothing else: how many seats, what they
 * cost, when the next invoice is, and the card. Who sits in them is Members,
 * and this page does not offer to take you there — two pages about one subject
 * each, rather than two pages pointing at one another.
 */
export function Organizations({
  me,
  organization,
  onRefresh
}: {
  me: Me;
  /** The organization being looked at — the one the rail is drawn for. */
  organization: Organization | null;
  onRefresh: () => Promise<void>;
}): React.JSX.Element {
  // The organization as the API last described it: `/me` carries a copy, and
  // buying seats has to move the figures on this page without a reload.
  const [held, setHeld] = useState<Organization | null>(organization);
  useEffect(() => setHeld(organization), [organization]);

  const mine = held ?? organization ?? administered(me);
  const administers = mine?.role === 'owner' || mine?.role === 'admin';

  if (!mine) {
    return (
      <Empty title="No organization on this account">
        An organization is chosen at sign-up and an account holds one. This page is its seats and its invoice, so
        there is nothing here until this account is in one.
      </Empty>
    );
  }

  if (!administers) {
    return (
      <Empty title="You hold a seat here">
        What the organization buys is the owner’s and the administrators’. Your seat is your licence, and there is
        nothing on it for you to pay.
      </Empty>
    );
  }

  // Priced from its own tier's list: an Enterprise seat is not a Team seat.
  const plans = mine.billing.tier === 'enterprise' ? me.config.enterprisePlans : me.config.seatPlans;
  const plan = plans.find((candidate) => candidate.id === mine.billing.plan);
  // **Paying**, not *working*. The two came apart the day an organization could
  // be given its seats: a beta tenant has active access, a paid-through date
  // and no subscription at all, and reading "has it bought seats?" off its
  // access told it the purchase was already made — so the page offered to
  // change a quantity that did not exist and the Worker refused every click.
  // A subscription is the only thing that says somebody is paying.
  const paying = hasBoughtSeats(mine.billing);
  // A trial is a grant the owner started (`trial.ts`), and it is said as one:
  // days left, seats out of five, and the plans underneath to buy from.
  const onTrial = !paying && mine.billing.plan === TRIAL_PLAN;
  const granted = !paying && !onTrial && mine.seatAccess !== 'none';
  const changed = async (next: Organization): Promise<void> => {
    setHeld(next);
    await onRefresh();
  };
  const filled = mine.seats;
  const paid = mine.billing.seatsPaid;

  return (
    <>
      <BillingStatus me={me} organization={mine} paying={paying} annual={plan?.id === 'annual'} />
      <StatStrip
        items={[
          { label: 'Seats bought', value: paying ? String(paid) : 'none' },
          { label: 'Seats filled', value: `${filled}${paying ? ` of ${paid}` : ''}` },
          { label: 'Per seat', value: paying && plan ? `$${plan.priceUsd}` : granted || onTrial ? '$0' : '\u2014' },
          {
            label: paying && plan?.id === 'annual' ? 'A year' : 'A month',
            value: paying && plan ? `$${plan.priceUsd * paid}` : '$0'
          }
        ]}
      />
      <div style={{ height: 20 }} />

      {!paying && !onTrial && trialOffered(mine) && <TrialOffer me={me} organization={mine} onStarted={changed} />}

      {paying ? (
        <>
          <SeatCount organization={mine} plan={plan} onChanged={changed} />
          <PeriodSwitch me={me} organization={mine} onMoved={changed} />
          {mine.billing.tier !== 'enterprise' && <EnterpriseUpgrade me={me} organization={mine} onMoved={changed} />}
        </>
      ) : (
        <BuySeats me={me} organization={mine} />
      )}

      <Card style={{ marginTop: 20, maxWidth: 620 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>{mine.name}</div>
        <div style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint, marginTop: 6 }}>
          {mine.slug} · {mine.role} · {mine.country} · {mine.billingEmail}
        </div>
        <p style={{ fontSize: 13, color: color.inkSubtle, margin: '12px 0 0', lineHeight: 1.6 }}>
          {paying
            ? `Invoices, the card and the VAT details are Stripe\u2019s${mine.billing.paidThrough ? `, and this period is paid through ${isoDay(mine.billing.paidThrough)}` : ''}.`
            : granted || onTrial
              ? `Nothing is charged while the ${onTrial ? 'trial' : 'grant'} holds. Buying seats is what starts an invoice.`
              : 'Nothing is charged until seats are bought. People can be invited and added before that — what waits is Airlock unlocking on their machines.'}
        </p>
        {paying && mine.billing.invoiceBilling && (
          <p style={{ fontSize: 13, color: color.inkSubtle, margin: '10px 0 0', lineHeight: 1.6 }}>
            Paid by invoice: each one is emailed to pay within 30 days
            {mine.billing.poNumber ? `, naming purchase order ${mine.billing.poNumber}` : ''}.
          </p>
        )}
        {paying && <Portal organization={mine} />}
      </Card>

      {/* The organization's own invoices, for whoever administers it — not
          only the owner who paid them, whose personal list has them too. */}
      {mine.billing.stripeCustomer && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Invoices</div>
          <BillingHistory path={apiPath`/orgs/${mine.id}/invoices`} />
        </div>
      )}

    </>
  );
}

/**
 * How many seats, as a number somebody sets.
 *
 * The one control on this page, and the answer to "I cannot see how to give a
 * seat back". Two floors are drawn rather than explained: it will not go under
 * the seats in use, and it will not go under one. The Worker refuses both as
 * well — this is the half a person can see before they click, not instead of
 * the check.
 *
 * Nothing is saved as the number moves. An owner clicking `−` four times is
 * making one decision, and four prorated invoices for the four steps of it
 * would be four decisions nobody took.
 */
function SeatCount({
  organization,
  plan,
  onChanged
}: {
  organization: Organization;
  plan: Plan | undefined;
  onChanged: (organization: Organization) => Promise<void>;
}): React.JSX.Element {
  const paid = organization.billing.seatsPaid;
  const [wanted, setWanted] = useState(paid);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => setWanted(paid), [paid]);

  const floor = Math.max(1, organization.seats);
  const moved = wanted !== paid;

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const answer = await api.send<{ organization: Organization }>(
        'PATCH',
        apiPath`/orgs/${organization.id}/seats`,
        { seats: wanted },
        {}
      );
      setNote(
        wanted > paid
          ? `${wanted} seats from today; the extra ${wanted - paid === 1 ? 'one is' : `${wanted - paid} are`} on the next invoice, prorated.`
          : `${paid - wanted} seat${paid - wanted === 1 ? '' : 's'} given back. The unused part of this period is credited against the next invoice.`
      );
      await onChanged(answer.organization);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ maxWidth: 620 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* The plan and its state are said once, under the page title (§43.3). */}
        <span style={{ fontSize: 16, fontWeight: 600 }}>Seats</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: `1px solid ${color.lineInput}`, borderRadius: radius.md, padding: 3 }}>
          <Step
            label="One seat fewer"
            sign="−"
            disabled={wanted <= floor}
            reason={wanted <= floor && organization.seats > 1 ? `${organization.seats} seats are given out — take one back on Members first` : undefined}
            onClick={() => setWanted((n) => Math.max(floor, n - 1))}
          />
          <input
            type="number"
            aria-label="Seats bought"
            value={wanted}
            min={floor}
            onChange={(event) => setWanted(Math.max(floor, Math.trunc(Number(event.target.value)) || floor))}
            style={{
              width: 64,
              height: 30,
              border: 0,
              background: 'transparent',
              textAlign: 'center',
              fontFamily: font.mono,
              fontSize: 15,
              color: color.ink
            }}
          />
          <Step label="One seat more" sign="+" onClick={() => setWanted((n) => n + 1)} />
        </div>
        <span style={{ fontSize: 13.5, color: color.inkSubtle }}>
          {plan ? `$${plan.priceUsd * wanted} ${plan.id === 'annual' ? 'a year' : 'a month'}` : '—'}
          {moved && plan ? ` · was $${plan.priceUsd * paid}` : ''}
        </span>
      </div>

      {/* Changing the number applies nothing (§43.2, §22): the confirmation
          band unfolds under the control with before → after and the price,
          Cancel then the verb, and only the verb buys or gives back. */}
      {moved && (
        <div
          role="group"
          aria-label="Confirm the seat change"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 12,
            padding: '12px 14px',
            border: `1px solid ${color.line}`,
            borderRadius: 12
          }}
        >
          <span style={{ flex: 1, minWidth: 220, fontSize: 13, color: color.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
            {paid} → {wanted} seats
            {plan
              ? ` · $${plan.priceUsd * paid} → $${plan.priceUsd * wanted} / ${plan.id === 'annual' ? 'year' : 'month'}, excl. VAT`
              : ''}
            {wanted > paid ? ' · the extra prorated today' : ' · the rest of the period credited'}
          </span>
          <GhostButton size="sm" onClick={() => setWanted(paid)} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton size="sm" onClick={() => void save()} disabled={busy}>
            {busy ? (wanted > paid ? 'Buying' : 'Giving back') : wanted > paid ? `Buy ${wanted - paid} more` : `Give ${paid - wanted} back`}
          </PrimaryButton>
        </div>
      )}

      <p style={{ fontSize: 13, color: color.inkSubtle, margin: '14px 0 0', lineHeight: 1.6 }}>
        {organization.seats >= wanted && wanted === paid
          ? `Every seat is given out — ${organization.seats} of ${paid}. Buy more here, and they can be given to anybody already in the organization on Members.`
          : `${organization.seats} of ${paid} ${organization.seats === 1 ? 'is' : 'are'} given out. A seat cannot be given back to us while somebody holds it: take it back from them on Members first, and the count can then come down.`}
      </p>
      {error && <p style={{ fontSize: 13, color: color.badText, margin: '12px 0 0' }}>{error}</p>}
      {note && <p style={{ fontSize: 13, color: color.accentText, margin: '12px 0 0', lineHeight: 1.6 }}>{note}</p>}
    </Card>
  );
}

function Step({
  label,
  sign,
  onClick,
  disabled,
  reason
}: {
  label: string;
  sign: string;
  onClick: () => void;
  disabled?: boolean;
  /** Why the step is held, said where the pointer is (§43.2, the bound). */
  reason?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={reason}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 30,
        height: 30,
        border: 0,
        borderRadius: radius.sm,
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: 16,
        color: disabled ? color.inkGhost : color.ink,
        cursor: disabled ? 'default' : 'pointer'
      }}
    >
      {sign}
    </button>
  );
}

/** The first purchase: a period, a number of seats, and Stripe. */
function BuySeats({ me, organization }: { me: Me; organization: Organization }): React.JSX.Element {
  const [seats, setSeats] = useState(Math.max(1, organization.seats));
  // Team or Enterprise: the same seats, two price lists (`tier.ts`).
  const [tier, setTier] = useState<'team' | 'enterprise'>('team');
  const plans = tier === 'enterprise' ? me.config.enterprisePlans : me.config.seatPlans;
  const [busy, setBusy] = useState<string | null>(null);
  // The refusal, with the plan whose button asked: it is said under that
  // button, not under both cards.
  const [error, setError] = useState<{ plan: string; text: string } | null>(null);

  const buy = async (plan: string): Promise<void> => {
    setBusy(plan);
    setError(null);
    try {
      const answer = await api.post<{ url: string }>(apiPath`/orgs/${organization.id}/checkout`, { plan, seats, tier });
      window.location.href = answer.url;
    } catch (failure) {
      setError({ plan, text: failure instanceof ApiError ? failure.message : 'That did not work.' });
      setBusy(null);
    }
  };

  return (
    <>
      <TierChoice me={me} tier={tier} onChange={setTier} />
      <Card style={{ maxWidth: 620 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>How many seats?</div>
        <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '6px 0 0', lineHeight: 1.6 }}>
          One perpetual key per person, on up to two machines each, and one invoice for the organization. Buy for the
          people you have and the ones you are about to hire — a seat sits empty until somebody is invited to it, and
          you can add or give back seats here at any time.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: `1px solid ${color.lineInput}`, borderRadius: radius.md, padding: 3 }}>
            <Step
              label="One seat fewer"
              sign="−"
              disabled={seats <= Math.max(1, organization.seats)}
              onClick={() => setSeats((n) => Math.max(Math.max(1, organization.seats), n - 1))}
            />
            <input
              type="number"
              aria-label="Seats to buy"
              value={seats}
              min={Math.max(1, organization.seats)}
              onChange={(event) =>
                setSeats(Math.max(Math.max(1, organization.seats), Math.trunc(Number(event.target.value)) || 1))
              }
              style={{
                width: 64,
                height: 30,
                border: 0,
                background: 'transparent',
                textAlign: 'center',
                fontFamily: font.mono,
                fontSize: 15,
                color: color.ink
              }}
            />
            <Step label="One seat more" sign="+" onClick={() => setSeats((n) => n + 1)} />
          </div>
          <span style={{ fontSize: 13, color: color.inkFaint }}>
            {organization.seats === 0
              ? 'Nobody holds one yet — you give them out here once they are bought, yours included.'
              : `${organization.seats} in use today — the count cannot start below that.`}
          </span>
        </div>
      </Card>

      <div className="al-two" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 20, marginTop: 20 }}>
        {plans.map((plan) => (
          <Card key={plan.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18, fontWeight: 600 }}>{plan.label}</span>
              {plan.id === 'annual' && <Badge tone="warn">Save 17%</Badge>}
            </div>
            {/* The whole bill, so `billed` and not `period`: this number is
                what the seat picker above adds up to, and putting it under
                `/ seat / month` told somebody buying two seats that each one
                costs thirty dollars. The per-seat price is on the line below,
                which is where a breakdown belongs. */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 18 }}>
              <span style={{ fontSize: t.priceApp, letterSpacing: '-.03em', fontWeight: 600 }}>
                ${plan.priceUsd * seats}
              </span>
              <span style={{ fontSize: 14, color: color.inkSubtle }}>{plan.billed} excl. VAT</span>
            </div>
            <div style={{ fontSize: 13.5, color: color.inkSubtle, marginTop: 8, lineHeight: 1.6 }}>
              {seats} seat{seats === 1 ? '' : 's'} at ${plan.priceUsd} {plan.period}. Adding one later is prorated for
              the rest of the period; giving one back is credited.
            </div>
            {!plan.onSale ? (
              <div style={{ fontSize: 13, color: color.warnText, marginTop: 12 }}>
                Not on sale on this deployment — no Stripe price is configured for {tier === 'enterprise' ? 'Enterprise seats' : 'seats'}.
              </div>
            ) : (
              <div style={{ marginTop: 20 }}>
                <PrimaryButton full size="lg" onClick={() => void buy(plan.id)}>
                  {busy === plan.id ? 'Buying' : `Buy ${seats} ${plan.id === 'annual' ? 'yearly' : 'monthly'}`}
                </PrimaryButton>
                {error?.plan === plan.id && (
                  <p role="alert" style={{ fontSize: 13, color: color.badText, margin: '10px 0 0', lineHeight: 1.5 }}>
                    {error.text}
                  </p>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}

/**
 * Team or Enterprise, before the seats are counted: the choice cards of the
 * redesign (§43.1) — two radios in a radiogroup, the chosen one ringed 2px
 * teal and never filled, ← → moving the choice. The question is which
 * product; the cards below then price the one chosen.
 */
function TierChoice({
  me,
  tier,
  onChange
}: {
  me: Me;
  tier: 'team' | 'enterprise';
  onChange: (tier: 'team' | 'enterprise') => void;
}): React.JSX.Element {
  const [hover, setHover] = useState<string | null>(null);
  const price = (plans: Plan[]): string => {
    const monthly = plans.find((plan) => plan.id === 'monthly') ?? plans[0];
    return monthly ? `$${monthly.priceUsd} ${monthly.period}` : '';
  };
  const options: { value: 'team' | 'enterprise'; label: string; price: string; note: string }[] = [
    { value: 'team', label: 'Team', price: price(me.config.seatPlans), note: 'Seats, workspaces, settings and the audit trail.' },
    {
      value: 'enterprise',
      label: 'Enterprise',
      price: price(me.config.enterprisePlans),
      note: 'Team, plus single sign-on, SCIM, enforced two-factor and paying by invoice.'
    }
  ];
  const onKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const next = tier === 'team' ? 'enterprise' : 'team';
    onChange(next);
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-tier="${next}"]`)?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label="Plan"
      className="al-two"
      onKeyDown={onKey}
      style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12, maxWidth: 620, marginBottom: 20 }}
    >
      {options.map((option) => {
        const chosen = tier === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={chosen}
            tabIndex={chosen ? 0 : -1}
            data-tier={option.value}
            onClick={() => onChange(option.value)}
            onMouseEnter={() => setHover(option.value)}
            onMouseLeave={() => setHover(null)}
            style={{
              textAlign: 'left',
              background: color.surface,
              // Chosen is a 2px teal ring (selection, as the plan card §39.3);
              // the 1px hairline otherwise, darker under the pointer.
              border: chosen ? `2px solid ${color.accentText}` : `1px solid ${hover === option.value ? color.inkGhost : color.line}`,
              borderRadius: 14,
              padding: chosen ? '15px 17px' : '16px 18px',
              fontFamily: 'inherit',
              color: color.ink,
              cursor: 'pointer',
              transform: hover === option.value && !chosen ? 'translateY(-1px)' : 'none',
              transition: 'border-color .12s, transform .3s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{option.label}</span>
              <span style={{ flex: 1 }} />
              <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkMuted }}>{option.price}</span>
            </div>
            <div style={{ fontSize: 13, color: color.inkMuted, marginTop: 4, lineHeight: 1.5 }}>{option.note}</div>
          </button>
        );
      })}
    </div>
  );
}

/** Stripe’s own page, named for what is on it rather than for Stripe. */
function Portal({ organization }: { organization: Organization }): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <div style={{ marginTop: 18 }}>
        <GhostButton
          onClick={() => {
            setBusy(true);
            setError(null);
            void api
              .post<{ url: string }>(apiPath`/orgs/${organization.id}/billing/portal`)
              .then((answer) => {
                window.location.href = answer.url;
              })
              .catch((failure: unknown) => {
                setError(failure instanceof ApiError ? failure.message : 'That did not work.');
                setBusy(false);
              });
          }}
        >
          {busy ? 'Opening' : 'Card, VAT details and invoices'}
        </GhostButton>
      </div>
      {error && <p style={{ fontSize: 13, color: color.badText, margin: '12px 0 0' }}>{error}</p>}
    </>
  );
}

export function Seats({
  me,
  orgId,
  onRefresh
}: {
  me: Me;
  orgId: string | null;
  onRefresh: () => Promise<void>;
}): React.JSX.Element {
  const [dialog, ask] = useConfirm();
  const chosen = orgId ?? me.organizations[0]?.id ?? null;
  const [seats, setSeats] = useState<Seat[] | null>(null);
  const [failed, setFailed] = useState(false);
  const phase = useLoadPhase(seats === null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [invitee, setInvitee] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The last invitation sent from this page, so its link can be copied. */
  const [sent, setSent] = useState<Invitation | null>(null);
  /** The person about to be deactivated, while the dialog asks. The one move
   *  on this page that is worth stopping somebody over: taking a seat back is
   *  undone with the button next to it, and this closes the pages on them. */
  const [deactivating, setDeactivating] = useState<Seat | null>(null);
  /** The member the owner is about to hand the organization to, while the dialog asks. */
  const [handingTo, setHandingTo] = useState<Seat | null>(null);
  /** What the hand-over said once it was done — who owns it now, and whether they were told. */
  const [handedOver, setHandedOver] = useState<string | null>(null);
  // Wide enough that the seat column's badge and its phrase sit on one line,
  // and that "Take seat back" and "Deactivate" do not stack.
  // The role column holds the owner's *Make admin* under the word, so it is as
  // wide as that link rather than as the word.
  const columns = '1fr 120px 230px 150px 215px';

  const load = async (): Promise<void> => {
    if (!chosen) return;
    const answer = await api.get<{ organization: Organization | null; seats: Seat[] }>(apiPath`/orgs/${chosen}/seats`);
    setSeats(answer.seats);
    setOrganization(answer.organization);
  };

  useEffect(() => {
    void load().catch(() => {
      setSeats([]);
      setFailed(true);
    });
    // `chosen` is the only input this read has.
  }, [chosen]);

  if (!chosen) {
    return (
      <Empty title="No organization yet">Create one on the Organizations page, and its seats appear here.</Empty>
    );
  }
  if (failed && seats?.length === 0) {
    return (
      <PageError
        onRetry={() =>
          load().then(
            () => setFailed(false),
            () => setFailed(true)
          )
        }
      />
    );
  }
  if (seats === null || phase !== 'content') {
    return phase === 'skeleton' ? <SkeletonRows columns="minmax(0,1.6fr) 1fr 1fr 1fr 40px" /> : <></>;
  }

  const myRole = me.organizations.find((candidate) => candidate.id === chosen)?.role;
  const canAdminister = myRole === 'owner' || myRole === 'admin' || me.user.role === 'system_admin';
  // Who administers the organization is the owner's alone (`org-roles.ts`).
  const isOwner = myRole === 'owner';
  // Four states, each counted by name rather than as "everybody else": they are
  // four different facts about a person, and subtracting one from another
  // quietly filed somebody under whichever was left.
  const holding = seats.filter((seat) => seat.seatStatus === 'assigned').length;
  const seatless = seats.filter((seat) => seat.seatStatus === 'member').length;
  const invited = seats.filter((seat) => seat.seatStatus === 'invited').length;
  const suspended = seats.filter((seat) => seat.seatStatus === 'suspended').length;
  // The organization as the API just described it, which is where the money is.
  // Falling back to the copy in `/me` keeps this readable for a system
  // administrator, who is looking at a tenant they are not a member of.
  const held = organization ?? me.organizations.find((candidate) => candidate.id === chosen) ?? null;
  const verified = Boolean(me.user.emailVerifiedAt);

  const invite = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    try {
      setSent(await api.post<Invitation>(apiPath`/orgs/${chosen}/seats`, { email: invitee }));
      setInvitee('');
      await load();
      await onRefresh();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  /** Send it again — a new link, so the first one stops working. */
  const again = async (seatId: string): Promise<void> => {
    setError(null);
    try {
      setSent(await api.post<Invitation>(apiPath`/orgs/${chosen}/seats/${seatId}/invite`));
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  /** Withdraw an invitation nobody took. The only row that is ever deleted. */
  const withdraw = async (seatId: string, email: string): Promise<void> => {
    // Asked first: the link stops working (redesign, proposal 16).
    const agreed = await ask({
      title: `Withdraw the invitation to ${email}?`,
      lede: `The invitation link stops working, and ${email} can no longer join with it. Nothing was charged.`,
      confirm: 'Withdraw',
      danger: true
    });
    if (!agreed) return;
    setError(null);
    try {
      await api.del(apiPath`/orgs/${chosen}/seats/${seatId}`);
      await load();
      await onRefresh();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  /**
   * Move somebody between the three states: holding a seat, here without one,
   * or deactivated. One call for all of them — the browser says what it wants
   * to be true and the Worker works out which verb that is, so the page cannot
   * get the pairing wrong for a row whose state it read a moment ago.
   */
  const setStatus = async (seatId: string, seatStatus: 'assigned' | 'member' | 'suspended'): Promise<void> => {
    setError(null);
    setDeactivating(null);
    try {
      const answer = await api.send<{ organization: Organization | null; seats: Seat[] }>(
        'PATCH',
        apiPath`/orgs/${chosen}/seats/${seatId}`,
        { seatStatus },
        {}
      );
      setSeats(answer.seats);
      setOrganization(answer.organization);
      await onRefresh();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  // Bought, granted or on a trial, and what is free against it — the Worker's
  // `freeSeats`, said once on this side (`seatFigures`). Counted against the
  // seats actually **held**, which is what the Worker counts: this was
  // `seats.length`, every row in the table, so every invitation waiting and
  // every deactivated colleague made the page claim a seat was taken that
  // nobody was in.
  const figures = seatFigures(held, holding, me.config.trial.seats);
  const free = figures.free;
  // Whether a full house means "buy another" rather than "buy the first": an
  // organization paying, or on a trial, has seats to run out of.
  const paying = hasBoughtSeats(held?.billing) || held?.billing.plan === TRIAL_PLAN;

  return (
    <>
      {dialog}
      <StatStrip
        items={[
          { label: 'Seats bought', value: figures.bought },
          { label: 'Seats held', value: String(holding) },
          // The figure this page exists to make possible, and the one an owner
          // acts on: people who are here and cannot work yet.
          ...(seatless > 0 ? [{ label: 'Without a seat', value: String(seatless) }] : []),
          { label: 'Invitations waiting', value: String(invited) },
          // Shown only where there is one to give: a column of zeroes is a page
          // telling somebody about a state they are not in.
          ...(suspended > 0 ? [{ label: 'Deactivated', value: String(suspended) }] : []),
          // An empty seat is a seat somebody paid for, so it is counted rather
          // than left to be worked out from the other figures.
          { label: 'Seats free', value: free === null ? '\u2014' : String(free) }
        ]}
      />
      <div style={{ height: 20 }} />
      <Table
        columns={columns}
        header={['Member', 'Role', 'Seat', 'Activity', '']}
      >
        {seats.map((seat) => (
          <Row key={seat.id} columns={columns}>
            {/* `minWidth: 0` lets this column give way to the fixed ones; the
                ellipsis is what stops a long address bleeding across the role
                beside it once it has. */}
            <div style={{ minWidth: 0 }}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seat.email}</div>
              {seat.invitedBy && seat.seatStatus === 'invited' && (
                <div
                  style={{
                    fontFamily: font.mono,
                    fontSize: 11.5,
                    color: color.inkFaint,
                    marginTop: 3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  invited by {seat.invitedBy}
                </div>
              )}
            </div>
            <RoleCell
              orgId={chosen}
              seat={seat}
              owner={isOwner}
              onChanged={(answer) => {
                setError(null);
                setSeats(answer.seats);
                setOrganization(answer.organization);
              }}
              onError={setError}
              onHandOver={setHandingTo}
            />
            {/* One column, and it is what this person is to the organization
                today: whether they are here, whether they hold a seat, and
                whether it works — the whole of what an owner wants to know
                about a row of this table. */}
            <StatusCell
              tone={(SEAT_STATE[seat.seatStatus] ?? SEAT_STATE.invited).tone}
              ring={(SEAT_STATE[seat.seatStatus] ?? SEAT_STATE.invited).ring}
              word={(SEAT_STATE[seat.seatStatus] ?? SEAT_STATE.invited).word}
              detail={SEAT_WORD(seat)}
            />
            {seat.seatStatus === 'invited' ? (
              <DateCell at={seat.invitedAt} prefix="invited" style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
            ) : (
              <DateCell at={seat.lastActiveAt} prefix="seen" style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
            )}
            {/* Two decisions, kept apart on purpose, because they are not the
                same thing and an owner should not have to choose one to get
                the other: **the seat** is what somebody works with, and
                **being here** is whether they are a colleague at all. Taking
                a seat back is reversible with the button that replaces it;
                deactivating asks first. Neither ever deletes anybody — see
                `suspendSeat`. An invitation nobody took is the one row that is
                withdrawn, because there is no person in it. */}
            {canAdminister ? (
              // One text action, the rest — destructive last, red — in « More
              // actions »; a row with no menu keeps its place so the links align
              // from one row to the next (redesign §41.3, proposal 1).
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, whiteSpace: 'nowrap' }}>
                {seat.seatStatus === 'invited' && <RowAction onClick={() => void again(seat.id)}>Send again</RowAction>}
                {/* The owner is offered this one and no other. An organization
                    that has bought nothing holds no seats at all, its owner's
                    included, so the person who buys the first seats needs a way
                    to take one — and the verbs in the menu would leave their own
                    organization with nobody who can administer it. */}
                {seat.seatStatus === 'member' && (
                  <RowAction onClick={() => void setStatus(seat.id, 'assigned')}>Give a seat</RowAction>
                )}
                {seat.role !== 'owner' && seat.seatStatus === 'assigned' && (
                  <RowAction onClick={() => void setStatus(seat.id, 'member')}>Take seat back</RowAction>
                )}
                {seat.role !== 'owner' && seat.seatStatus === 'suspended' && (
                  <RowAction onClick={() => void setStatus(seat.id, 'member')}>Reactivate</RowAction>
                )}
                {seat.seatStatus === 'invited' ? (
                  <MenuButton
                    icon
                    align="right"
                    label="More actions"
                    items={[{ label: 'Withdraw', danger: true, onPick: () => void withdraw(seat.id, seat.email) }]}
                  />
                ) : seat.role !== 'owner' && (seat.seatStatus === 'assigned' || seat.seatStatus === 'member') ? (
                  <MenuButton
                    icon
                    align="right"
                    label="More actions"
                    items={[{ label: 'Deactivate', danger: true, onPick: () => setDeactivating(seat) }]}
                  />
                ) : (
                  <span style={{ width: 28 }} />
                )}
              </div>
            ) : (
              <span />
            )}
          </Row>
        ))}
      </Table>

      {deactivating && (
        <Modal
          title={`Deactivate ${deactivating.email}?`}
          lede={`They stop being a member of ${
            organization?.name ?? 'this organization'
          }: its pages close for them, their key stops working and their machines stop acting here at their next check.${
            deactivating.seatStatus === 'assigned' ? ' Their seat comes free for somebody else.' : ''
          } Nothing is deleted — their teams, the workspaces shared with them by name and everything they published are kept — so bringing them back is one click. Nothing is charged or credited either: the seats were bought either way.`}
          confirm="Deactivate"
          danger
          onConfirm={() => void setStatus(deactivating.id, 'suspended')}
          onCancel={() => setDeactivating(null)}
        />
      )}

      {handingTo && (
        <HandOver
          orgId={chosen}
          orgName={organization?.name ?? held?.name ?? 'this organization'}
          seat={handingTo}
          onCancel={() => setHandingTo(null)}
          onDone={(answer) => {
            setHandingTo(null);
            setSeats(answer.seats);
            setOrganization(answer.organization);
            setHandedOver(
              `${handingTo.email} owns ${answer.organization?.name ?? 'the organization'} now, and you are an administrator.${
                answer.delivery.sent ? ' They have been sent an email.' : ' No email went out — let them know.'
              }`
            );
            void onRefresh();
          }}
        />
      )}
      {handedOver && (
        <div style={{ maxWidth: 760, marginTop: 18 }}>
          <Callout tone="teal" title="Handed over">
            {handedOver}
          </Callout>
        </div>
      )}

      {/* The verb that adds a row, under the rows it adds to — the shape the
          teams page, the workspace's access and the deployment's own
          administrators all use. It was in the table's toolbar, which is the
          one place on this page that is not about a person. */}
      {canAdminister && verified && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
          <label htmlFor="invite-email" style={{ fontSize: 13, color: color.inkSubtle }}>
            Invite somebody to {organization?.name ?? 'this organization'}
          </label>
          <form onSubmit={(event) => void invite(event)} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Input
              id="invite-email"
              type="email"
              required
              value={invitee}
              onChange={(event) => setInvitee(event.target.value)}
              placeholder="teammate@acme-platform.test"
              style={{ width: 'auto', minWidth: 240, fontSize: 13.5, padding: '9px 12px', borderRadius: radius.md }}
            />
            <PrimaryButton size="sm" type="submit">
              Invite
            </PrimaryButton>
          </form>
          {/* What an invitation costs, said before the address is typed rather
              than after: nothing, ever. The seat is the separate decision, and
              the sentence names it so that nobody invites four people and then
              wonders why none of them can open Airlock. */}
          <span style={{ fontSize: 12.5, color: color.inkFaint }}>
            {free === null
              ? 'Nothing is charged for an invitation. Give them a seat once they accept.'
              : free > 0
                ? `Nothing is charged for an invitation — ${free} seat${free === 1 ? ' is' : 's are'} free to give once they accept.`
                : paying
                  ? 'Nothing is charged for an invitation. Every seat is taken, so buy one on Billing before giving them theirs.'
                  : 'Nothing is charged for an invitation. No seats have been bought yet \u2014 that is Billing, and they can be given out here afterwards.'}
          </span>
        </div>
      )}

      {/* What actually happened to the invitation, under the form that sent
          it (§41.5). Sent, one sentence; the mail did not go, the link is the
          invitation, so it waits in a field to copy. */}
      {sent && (
        <div style={{ marginTop: 12 }}>
          <InviteResult email={sent.seat.email} url={sent.url} delivery={sent.delivery} onDismiss={() => setSent(null)} />
        </div>
      )}

      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: '16px 0 0' }}>{error}</p>}

      {isOwner && (
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '16px 0 0', lineHeight: 1.6 }}>
          Administrators invite people, give out seats, see the audit and manage billing. Only you choose who is one, and
          only you can hand the organization to somebody else — with Make owner, which asks for its name.
        </p>
      )}

      {!canAdminister && (
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '16px 0 0', lineHeight: 1.6 }}>
          You are a member here. Inviting people and giving out seats is the owner’s and the administrators’.
        </p>
      )}

      {/* What is waiting, where anything is. This used to be the refusal that
          stood in for the invite box — "no seats have been bought, so nothing
          on this page can be filled" — and the box is always there now. What
          is left is the one thing an owner still has to do something about:
          colleagues who are here and cannot work, which no amount of inviting
          fixes. Said only when somebody is actually in that state. */}
      {canAdminister && seatless > 0 && free !== null && free < seatless && (
        <div style={{ marginTop: 20, maxWidth: 760 }}>
          <Callout
            tone="amber"
            title={`${seatless - free} member${seatless - free === 1 ? '' : 's'} here cannot work yet`}
          >
            {seatless} {seatless === 1 ? 'person is' : 'people are'} in {organization?.name ?? 'this organization'}{' '}
            without a seat, and{' '}
            {free === 0
              ? paying
                ? 'there is none free to give'
                : 'this organization has not bought any'
              : `only ${free} ${free === 1 ? 'is' : 'are'} free`}.
            They are in their teams and the workspaces shared with them are waiting; what a seat adds is Airlock
            unlocking on their machine. Seats are bought on <strong>Billing</strong>, and given from the list above.
          </Callout>
        </div>
      )}
      {canAdminister && !verified && (
        <div style={{ marginTop: 20, maxWidth: 760 }}>
          <Callout tone="amber" title="Confirm your email address first">
            An invitation goes out over our name and our sending domain, so it waits until the address sending it has
            been proved. The strip at the top of the page will send the link again.
          </Callout>
        </div>
      )}
      {canAdminister && !me.config.mail && (
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '16px 0 0', lineHeight: 1.6 }}>
          This deployment cannot send email, so an invitation is a link to pass on rather than a message. Setting
          <span style={{ fontFamily: font.mono }}> RESEND_API_KEY </span> and
          <span style={{ fontFamily: font.mono }}> MAIL_FROM </span> is what turns it into one.
        </p>
      )}
    </>
  );
}
